import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Alert, Button, Container, Form, ProgressBar, Image, Table, Spinner, Navbar } from 'react-bootstrap';
import { BlobServiceClient } from '@azure/storage-blob';
import { storageConfig } from '../config/authConfig';
import { BlobItem } from '../models/BlobItem';
import FileViewer from './FileViewer';

const PublicUpload: React.FC = () => {
  const url = new URL(window.location.href);
  const containerName = url.searchParams.get('container') || '';
  const sas = url.searchParams.get('sas') || '';

  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [blobs, setBlobs] = useState<BlobItem[]>([]);
  const [loadingBlobs, setLoadingBlobs] = useState<boolean>(false);
  const [showFileViewer, setShowFileViewer] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<BlobItem | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const containerClient = useMemo(() => {
    if (!containerName || !sas) return null;
    const url = `https://${storageConfig.accountName}.blob.core.windows.net/${containerName}?${sas}`;
    return new BlobServiceClient(url).getContainerClient('');
  }, [containerName, sas]);

  // Fetch blobs when component mounts and after successful uploads
  useEffect(() => {
    if (containerClient) {
      fetchBlobs();
    }
  }, [containerClient]);

  const fetchBlobs = async (): Promise<void> => {
    if (!containerClient) return;
    
    setLoadingBlobs(true);
    setError('');
    
    try {
      const blobList: BlobItem[] = [];
      for await (const blob of containerClient.listBlobsFlat()) {
        const blobClient = containerClient.getBlobClient(blob.name);
        
        blobList.push({
          name: blob.name,
          contentLength: blob.properties.contentLength || 0,
          contentType: blob.properties.contentType || 'unknown',
          lastModified: blob.properties.lastModified || new Date(),
          url: blobClient.url
        });
      }
      setBlobs(blobList);
    } catch (err) {
      console.error('Error fetching blobs:', err);
      // Don't show error for blob listing - it might be a permissions issue
      // but we still want to allow uploads
    } finally {
      setLoadingBlobs(false);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSelectedFiles(e.target.files);
    setMessage('');
    setError('');
  };

  const handleDrag = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const fileList = e.dataTransfer.files;
      setSelectedFiles(fileList);
      setMessage('');
      setError('');
    }
  };

  const upload = async (): Promise<void> => {
    if (!containerClient || !selectedFiles || selectedFiles.length === 0) return;
    setIsUploading(true);
    setProgress(0);
    setMessage('');
    setError('');

    try {
      let uploaded = 0;
      const totalFiles = selectedFiles.length;
      
      for (const file of Array.from(selectedFiles)) {
        const blobClient = containerClient.getBlockBlobClient(file.name);
        await blobClient.uploadData(await file.arrayBuffer(), {
          blobHTTPHeaders: { blobContentType: file.type },
          onProgress: (ev) => {
            if (totalFiles === 1 && ev.loadedBytes && file.size) {
              setProgress(Math.round((ev.loadedBytes / file.size) * 100));
            } else {
              // For multiple files, show overall progress
              const fileProgress = (uploaded / totalFiles) * 100;
              setProgress(Math.round(fileProgress));
            }
          }
        });
        uploaded++;
        
        // Update progress for multiple files
        if (totalFiles > 1) {
          setProgress(Math.round((uploaded / totalFiles) * 100));
        }
      }
      
      setMessage(`Successfully uploaded ${uploaded} file${uploaded > 1 ? 's' : ''} to ${containerName}.`);
      setSelectedFiles(null);
      if (inputRef.current) inputRef.current.value = '';
      setProgress(100);
      
      // Refresh the blob list after successful upload
      await fetchBlobs();
      
      // Auto-clear success message after 5 seconds
      setTimeout(() => setMessage(''), 5000);
    } catch (e: any) {
      console.error(e);
      setError('Upload failed. The link may be expired or invalid.');
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getTotalSize = (): string => {
    if (!selectedFiles) return '';
    let total = 0;
    for (let i = 0; i < selectedFiles.length; i++) {
      total += selectedFiles[i].size;
    }
    return formatFileSize(total);
  };

  const removeFile = (index: number): void => {
    if (!selectedFiles) return;
    const dt = new DataTransfer();
    for (let i = 0; i < selectedFiles.length; i++) {
      if (i !== index) {
        dt.items.add(selectedFiles[i]);
      }
    }
    setSelectedFiles(dt.files.length > 0 ? dt.files : null);
    if (inputRef.current) {
      inputRef.current.files = dt.files;
    }
  };

  const handleDownloadBlob = async (blobName: string): Promise<void> => {
    if (!containerClient) return;
    
    try {
      const blobClient = containerClient.getBlobClient(blobName);
      const downloadResponse = await blobClient.download();
      
      let dataBlob: Blob | null = null;

      if (downloadResponse.blobBody) {
        dataBlob = await downloadResponse.blobBody;
      } else if (downloadResponse.readableStreamBody) {
        const webStream = downloadResponse.readableStreamBody as unknown as ReadableStream;
        const resp = new Response(webStream as any);
        dataBlob = await resp.blob();
      }

      if (!dataBlob) {
        throw new Error('Failed to download file');
      }

      // Create a blob URL for the downloaded data
      const blobUrl = URL.createObjectURL(dataBlob);
      
      // Create and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = blobName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file. Please try again.');
    }
  };

  const handleViewFile = (blob: BlobItem): void => {
    setSelectedFile(blob);
    setShowFileViewer(true);
  };

  const handleCloseViewer = (): void => {
    setShowFileViewer(false);
    setSelectedFile(null);
  };

  // Filter blobs based on search term
  const filteredBlobs = blobs.filter(blob =>
    blob.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    blob.contentType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = (): void => {
    setSearchTerm('');
  };

  const isLinkValid = Boolean(containerName && sas);

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header - Same as admin */}
      <Navbar bg="white" variant="light" className="mb-4 border-bottom shadow-sm">
        <Container>
          <Navbar.Brand>
            <Image 
              src="/qualrisk-logo.webp" 
              alt="QualRisk AI" 
              width="167" 
              height="30" 
              className="d-inline-block align-top me-2" 
            />
            QAI Secure File Transfer Manager
          </Navbar.Brand>
          <Navbar.Toggle />
          <Navbar.Collapse className="justify-content-end">
            <Navbar.Text className="me-3">
              <span className="text-muted">Public Upload Portal</span>
            </Navbar.Text>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {!isLinkValid ? (
        /* Invalid Link State */
        <Container className="flex-grow-1 d-flex align-items-center justify-content-center">
          <div className="text-center py-5">
            <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '4rem' }}></i>
            <h4 className="mt-4 mb-3">Invalid Upload Link</h4>
            <p className="text-muted mb-4">
              The upload link appears to be invalid or has expired. 
              Please contact the administrator for a new link.
            </p>
            <Alert variant="warning" className="d-inline-block">
              <i className="bi bi-info-circle me-2"></i>
              Upload links are time-limited for security purposes
            </Alert>
          </div>
        </Container>
      ) : (
        /* Main Dashboard Layout - Same as admin */
        <div className="dashboard-content flex-grow-1">
          <div className="dashboard-layout">
            {/* Sidebar Panel - Same as admin */}
            <div className="sidebar-panel">
              <div className="container-sidebar">
                <div className="sidebar-header">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="sidebar-title">Container Access</div>
                  </div>
                </div>
                <div className="container-list-wrapper">
                  <div className="container-list">
                    <div className="container-item active">
                      <div className="container-info">
                        <div className="container-icon">
                          <i className="bi bi-folder2-open"></i>
                        </div>
                        <div className="container-details">
                          <div className="container-name">{containerName}</div>
                          <div className="container-meta">Public Upload Access</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Panel - Same as admin BlobList */}
            <div className="main-panel">
              <div className="h-100 d-flex flex-column">
                <div className="blob-header">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="blob-title">
                      <i className="bi bi-files me-2"></i>
                      Blobs in "{containerName}"
                    </h4>
                    <div>
                      <Form.Control
                        type="file"
                        multiple
                        ref={inputRef}
                        onChange={onFileChange}
                        style={{ display: 'none' }}
                        id="fileUpload"
                      />
                      <Button 
                        variant="primary" 
                        size="sm" 
                        onClick={() => inputRef.current?.click()}
                        disabled={isUploading}
                      >
                        <i className="bi bi-cloud-upload me-1"></i>
                        {isUploading ? 'Uploading...' : 'Upload File'}
                      </Button>
                    </div>
                  </div>
                  
                  {blobs.length > 0 && (
                    <div className="search-container">
                      <div className="search-input-wrapper">
                        <i className="bi bi-search search-icon"></i>
                        <Form.Control
                          type="text"
                          placeholder="Search files by name or type..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                          className="search-input"
                        />
                        {searchTerm && (
                          <button
                            className="search-clear-btn"
                            onClick={clearSearch}
                            title="Clear search"
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        )}
                      </div>
                      {searchTerm && (
                        <div className="search-results-info">
                          {filteredBlobs.length} of {blobs.length} files
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="blob-content flex-grow-1">
                  {/* Upload Progress */}
                  {isUploading && (
                    <Alert variant="info" className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Uploading files...</span>
                        <span>{progress}%</span>
                      </div>
                      <ProgressBar now={progress} animated={isUploading} />
                    </Alert>
                  )}

                  {/* Messages */}
                  {message && <Alert variant="success">{message}</Alert>}
                  {error && <Alert variant="danger">{error}</Alert>}

                  {/* File List */}
                  {blobs.length === 0 ? (
                    <div className="empty-state">
                      <i className="bi bi-file-earmark empty-icon"></i>
                      <p className="empty-text">No files found in this container</p>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => inputRef.current?.click()}
                        disabled={isUploading}
                      >
                        <i className="bi bi-cloud-upload me-1"></i>
                        Upload your first file
                      </Button>
                    </div>
                  ) : filteredBlobs.length === 0 ? (
                    <div className="empty-state">
                      <i className="bi bi-search empty-icon"></i>
                      <p className="empty-text">No files match your search</p>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={clearSearch}
                      >
                        <i className="bi bi-x-circle me-1"></i>
                        Clear search
                      </Button>
                    </div>
                  ) : (
                    <div className="blob-table-wrapper">
                      <Table className="blob-table" responsive>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Size</th>
                            <th>Type</th>
                            <th>Last Modified</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredBlobs.map((blob) => (
                            <tr key={blob.name}>
                              <td>
                                <div className="file-name-cell">
                                  <i className="bi bi-file-earmark-text me-2 text-muted flex-shrink-0"></i>
                                  <span className="file-name-text" title={blob.name}>
                                    {blob.name}
                                  </span>
                                </div>
                              </td>
                              <td>{formatFileSize(blob.contentLength)}</td>
                              <td>
                                <span className="badge bg-light text-dark type-badge" title={blob.contentType}>
                                  {blob.contentType}
                                </span>
                              </td>
                              <td title={blob.lastModified.toLocaleString()}>
                                {blob.lastModified.toLocaleDateString()}
                              </td>
                              <td>
                                <div className="blob-actions">
                                  <button 
                                    className="btn btn-icon btn-view"
                                    onClick={() => handleViewFile(blob)}
                                    title="View file"
                                  >
                                    <i className="bi bi-eye"></i>
                                  </button>
                                  <button 
                                    className="btn btn-icon btn-download"
                                    onClick={() => handleDownloadBlob(blob.name)}
                                    title="Download file"
                                  >
                                    <i className="bi bi-download"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer - Same as admin */}
      <footer className="bg-light py-3">
        <Container>
          <div className="text-center text-muted">
            &copy; {new Date().getFullYear()} QAI Secure File Transfer Manager
          </div>
        </Container>
      </footer>

      {/* File Viewer Modal */}
      {selectedFile && (
        <FileViewer
          show={showFileViewer}
          onHide={handleCloseViewer}
          containerName={containerName}
          fileName={selectedFile.name}
          contentType={selectedFile.contentType}
          fileSize={selectedFile.contentLength}
        />
      )}
    </div>
  );
};

export default PublicUpload;



